import React, { ReactElement, useEffect, useMemo, useState } from "react";

import {
  Tab,
  Header,
  Table,
  Button,
  Dropdown,
  Modal,
  Input,
  Pagination,
  Icon,
  TextArea,
} from "semantic-ui-react";

import moment from "moment";

import DateTextBox from "../common/DateTextBox";

import "./CardPhysiotherapistSignUpRequest.scss";

import { parseBeOrAdDate } from "react-lib/utils/dateUtils";

type CardPhysiotherapistSignUpRequestProps = {
  buttonLoadCheck?: any;
  onEvent: any;
  currentTherapists?: any;
  educationList?: any[];
  specialtyList?: any[];
  clinicalTermSet?: any;
  employeeDocuments?: any;
  auditLogList?: any[];
};

type PhysioRequest = {
  employee: any;
  actions: any;
};

const ICON = {
  IconUser: "/static/images/icon_user.png",
  IconRejectReason: "/static/images/icon_reject_reason.png",
  IconReject: "/static/images/icon_reject.png",
  IconEditHistory: "/static/images/icon_edit_history.png",
  IconEdit: "/static/images/icon_edit.png",
};

const COLOR = {
  primary: "var(--primary-theme-color)",
  ccc_gray: "#ccc",
  light_gray: "#f5f5f5",
  light_pink: "#ffd8d8",
  light_red: "#f24646",
  red: "#d31919",
  green: "#14ae5c",
  dark_orange: "#d06500",
};

const STATUS_OPTIONS = [
  { key: "pending", text: "รออนุมัติ", value: "S" },
  { key: "resubmitted", text: "ส่งข้อมูลเข้ามาตรวจสอบใหม่", value: "R" },
  { key: "all", text: "ทั้งหมด", value: "all" },
];

const ROWS_PER_PAGE = 15;

const EXCLUDE_FIELDS = [
  "edited",
  "first_name",
  "first_name_en",
  "last_name",
  "last_name_en",
  "name_code",
  "code",
];

const EDIT_DATA_MAPPING: Record<string, { main: string; sub: string }> = {
  // ข้อมูลส่วนตัว
  full_name: { main: "ข้อมูลส่วนตัว", sub: "ชื่อ-นามสกุล" },
  first_name: { main: "ข้อมูลส่วนตัว", sub: "ชื่อ (ไทย)" },
  last_name: { main: "ข้อมูลส่วนตัว", sub: "นามสกุล (ไทย)" },
  first_name_en: { main: "ข้อมูลส่วนตัว", sub: "ชื่อ (อังกฤษ)" },
  last_name_en: { main: "ข้อมูลส่วนตัว", sub: "นามสกุล (อังกฤษ)" },
  email: { main: "ข้อมูลส่วนตัว", sub: "อีเมล" },
  phone_no: { main: "ข้อมูลส่วนตัว", sub: "เบอร์โทรศัพท์" },
  name_code: { main: "ข้อมูลส่วนตัว", sub: "ชื่อโค้ด" },
  birth_date: { main: "ข้อมูลส่วนตัว", sub: "วันเดือนปีเกิด" },
  citizen_no: { main: "ข้อมูลส่วนตัว", sub: "เลขบัตรประชาชน" },
  gender: { main: "ข้อมูลส่วนตัว", sub: "เพศ" },

  // ข้อมูลทางวิชาชีพ
  licence: { main: "ข้อมูลทางวิชาชีพ", sub: "เลขที่ใบประกอบวิชาชีพ" },
  work_address: { main: "ข้อมูลทางวิชาชีพ", sub: "สถานที่ทำงาน" },
  specialties: { main: "ข้อมูลทางวิชาชีพ", sub: "ความเชี่ยวชาญและกลุ่มอาการ" },
};

const CardPhysiotherapistSignUpRequest: React.FC<CardPhysiotherapistSignUpRequestProps> = (
  props
) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [filterName, setFilterName] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filteredTherapists, setFilteredTherapists] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<PhysioRequest | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [rejectedFilterName, setRejectedFilterName] = useState("");
  const [rejectedFilterSubmitFrom, setRejectedFilterSubmitFrom] = useState("");
  const [rejectedFilterSubmitTo, setRejectedFilterSubmitTo] = useState("");
  const [rejectedFilterRejectFrom, setRejectedFilterRejectFrom] = useState("");
  const [rejectedFilterRejectTo, setRejectedFilterRejectTo] = useState("");
  const [blacklistFilterName, setBlacklistFilterName] = useState("");
  const [blacklistFilterSubmitFrom, setBlacklistFilterSubmitFrom] = useState("");
  const [blacklistFilterSubmitTo, setBlacklistFilterSubmitTo] = useState("");
  const [blacklistFilterRejectFrom, setBlacklistFilterRejectFrom] = useState("");
  const [blacklistFilterRejectTo, setBlacklistFilterRejectTo] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isRejectReasonModalOpen, setIsRejectReasonModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectTarget, setRejectTarget] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [zoom, setZoom] = useState(1);
  const [blacklistActivePage, setBlacklistActivePage] = useState(1);
  const [rejectedActivePage, setRejectedActivePage] = useState(1);
  const [isEditHistoryOpen, setIsEditHistoryOpen] = useState(false);
  const [isRejectLoading, setRejectLoading] = useState(false);

  const thaiMonths = [
    "",
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];

  const jobTypeList = [
    { key: "fulltime", text: "งานประจำ", value: "fulltime" },
    { key: "parttime", text: "งานพาร์ทไทม์", value: "parttime" },
    { key: "intern", text: "นักศึกษาฝึกงาน", value: "intern" },
    { key: "contract", text: "งานสัญญาจ้าง", value: "contract" },
    { key: "freelance", text: "ฟรีแลนซ์", value: "freelance" },
  ];

  const jobLevelList = [
    { key: "entry", text: "ระดับเริ่มต้น", value: "entry" },
    { key: "junior", text: "จูเนียร์", value: "junior" },
    { key: "mid", text: "ระดับกลาง", value: "mid" },
    { key: "senior", text: "ซีเนียร์", value: "senior" },
    { key: "manager", text: "ผู้จัดการ", value: "manager" },
    { key: "director", text: "ผู้อำนวยการ", value: "director" },
  ];

  const handleTabChange = (e: any, { activeIndex }: any) => {
    setFilterName("");
    setFilterStartDate("");
    setFilterEndDate("");
    setFilterStatus("all");

    setRejectedFilterName("");
    setRejectedFilterSubmitFrom("");
    setRejectedFilterSubmitTo("");
    setRejectedFilterRejectFrom("");
    setRejectedFilterRejectTo("");

    setBlacklistFilterName("");
    setBlacklistFilterSubmitFrom("");
    setBlacklistFilterSubmitTo("");
    setBlacklistFilterRejectFrom("");
    setBlacklistFilterRejectTo("");

    props.onEvent({
      message: "HandleGetTherapistList",
    });
    setActiveTabIndex(activeIndex);
  };

  const getDisplayName = (employee: any) =>
    employee?.full_name?.trim() ||
    `${employee?.first_name ?? ""} ${employee?.last_name ?? ""}`.trim();

  const pendingTherapists = (props.currentTherapists || [])
    .filter((t: any) => t.status === "S")
    .filter((t: any) => {
      if (!filterName) return true;
      return getDisplayName(t.employee).toLowerCase().includes(filterName.toLowerCase());
    })
    .filter((t: any) =>
      filterStatus && filterStatus !== "all" ? t.status === filterStatus : true
    );

  const totalPages = Math.max(1, Math.ceil(pendingTherapists.length / ROWS_PER_PAGE));

  const rejectedTherapists = (props.currentTherapists || [])
    .filter((t: any) => t.status === "X")
    .filter((t: any) => {
      if (!filterName) return true;
      return getDisplayName(t.employee).toLowerCase().includes(filterName.toLowerCase());
    })
    .filter((t: any) =>
      filterStatus && filterStatus !== "all" ? t.status === filterStatus : true
    );

  const totalRejectedPages = Math.max(1, Math.ceil(rejectedTherapists.length / ROWS_PER_PAGE));
  const currentRejectedData = rejectedTherapists.slice(
    (rejectedActivePage - 1) * ROWS_PER_PAGE,
    rejectedActivePage * ROWS_PER_PAGE
  );

  const blacklistTherapists = (props.currentTherapists || [])
    .filter((t: any) => t.status === "B")
    .filter((t: any) => {
      if (!filterName) return true;
      return getDisplayName(t.employee).toLowerCase().includes(filterName.toLowerCase());
    })
    .filter((t: any) =>
      filterStatus && filterStatus !== "all" ? t.status === filterStatus : true
    );

  const totalBlacklistPages = Math.max(1, Math.ceil(blacklistTherapists.length / ROWS_PER_PAGE));
  const currentBlacklistData = blacklistTherapists.slice(
    (blacklistActivePage - 1) * ROWS_PER_PAGE,
    blacklistActivePage * ROWS_PER_PAGE
  );

  const handlePageChange = (_e: any, { activePage }: any) => {
    setActivePage(activePage);
  };

  const handleBlacklistPageChange = (_e: any, { activePage }: any) => {
    setBlacklistActivePage(activePage);
  };

  const handleRejectedPageChange = (e: any, { activePage }: any) => {
    setRejectedActivePage(activePage);
  };

  const handlePreviewDocument = (type: string) => {
    const doc = props.employeeDocuments.find((d: any) => d.type === type);
    if (!doc) return alert("เอกสารไม่พบ");
    setPreviewUrl(doc.file);
    setPreviewOpen(true);
  };

  const currentPendingData = filteredTherapists.slice(
    (activePage - 1) * ROWS_PER_PAGE,
    activePage * ROWS_PER_PAGE
  );

  const emptyRowsCount = ROWS_PER_PAGE - currentPendingData.length;
  const emptyRows = Array.from({ length: emptyRowsCount > 0 ? emptyRowsCount : 0 });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("DD/MM/YYYY");
  };

  const getSchoolName = (schoolCode: string) => {
    const eduItems = props.clinicalTermSet?.edu_institution?.items || [];
    const found = eduItems.find((item: any) => item.code === schoolCode);
    return found ? found.name : schoolCode || "-";
  };

  const getCategoryName = (categoryCode: string) => {
    const eduItems = props.clinicalTermSet?.edu_category?.items || [];
    const found = eduItems.find((item: any) => item.code === categoryCode);
    return found ? found.name : categoryCode || "-";
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.2));

  const handleSearchClick = () => {
    const formatDate = (dateStr: string, isEnd = false) => {
      if (!dateStr) return undefined;
      const date = new Date(dateStr);
      if (isEnd) date.setHours(23, 59, 59, 999);
      return date.toISOString();
    };

    const startISO = formatDate(filterStartDate);
    const endISO = formatDate(filterEndDate, true);

    props.onEvent({
      message: "HandleSearchTherapistList",
      params: {
        name: filterName || undefined,
        status: filterStatus || undefined,
        startDate: startISO,
        endDate: endISO,
      },
    });
  };

  const handleRejectSearchClick = () => {
    const formatDate = (dateStr: string, isEnd = false) => {
      if (!dateStr) return undefined;
      const date = new Date(dateStr);
      if (isEnd) date.setHours(23, 59, 59, 999);
      return date.toISOString();
    };

    const startISO = formatDate(rejectedFilterSubmitFrom);
    const endISO = formatDate(rejectedFilterSubmitTo, true);
    const rejectStartISO = formatDate(rejectedFilterRejectFrom as string);
    const rejectEndISO = formatDate(rejectedFilterRejectTo as string, true);

    props.onEvent({
      message: "HandleSearchTherapistList",
      params: {
        name: rejectedFilterName || undefined,
        startDate: startISO,
        endDate: endISO,
        rejectStartDate: rejectStartISO,
        rejectEndDate: rejectEndISO,
      },
    });
  };

  const handleBlacklistSearchClick = () => {
    const formatDate = (dateStr: string, isEnd = false) => {
      if (!dateStr) return undefined;
      const date = new Date(dateStr);
      if (isEnd) date.setHours(23, 59, 59, 999);
      return date.toISOString();
    };

    const startISO = formatDate(blacklistFilterSubmitFrom);
    const endISO = formatDate(blacklistFilterSubmitTo, true);
    const rejectStartISO = formatDate(blacklistFilterRejectFrom as string);
    const rejectEndISO = formatDate(blacklistFilterRejectTo as string, true);

    props.onEvent({
      message: "HandleSearchTherapistList",
      params: {
        name: blacklistFilterName || undefined,
        startDate: startISO,
        endDate: endISO,
        rejectStartDate: rejectStartISO,
        rejectEndDate: rejectEndISO,
      },
    });
  };

  const actions = selectedRequest?.actions || [];
  const rejectActions = actions.filter((a: any) => a.action === "REJECT");
  const latestReject = rejectActions.length > 0 ? rejectActions[rejectActions.length - 1] : null;

  const getJobTypeText = (value: any) => {
    const found = jobTypeList.find((item) => item.value === value);
    return found ? found.text : value;
  };

  const getJobLevelText = (value: any) => {
    const found = jobLevelList.find((item) => item.value === value);
    return found ? found.text : value;
  };

  const getEducationLevelName = (code: string) => {
    const item = props.educationList?.find((opt) => opt.code === code);
    return item ? item.name : code;
  };

  const getEducationStatusName = (status: string) => {
    switch (status) {
      case "graduated":
        return "สำเร็จการศึกษา";
      case "studying":
        return "กำลังศึกษา";
      default:
        return status || "-";
    }
  };

  const renderPersonalDetail = (selectedRequest: any, setSelectedRequest: any) => {
    const specialtyNames =
      selectedRequest.employee?.extra?.specialties
        ?.map((code: string) => {
          return props.specialtyList?.find((s: any) => s.code === code)?.name || code;
        })
        .join(", ") || "-";

    return (
      <div className="card-physio-membership" style={{ padding: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 20,
            gap: "10px",
          }}
        >
          <Icon
            name="chevron left"
            size="large"
            style={{ cursor: "pointer", color: COLOR.primary }}
            onClick={() => {
              props.onEvent({
                message: "HandleGetTherapistList",
              });
              setSelectedRequest(null);
            }}
          />
          <Header style={{ color: "rgb(79,79,79)", margin: 0 }} as="h2">
            {selectedRequest.status === "X"
              ? "ถูกปฏิเสธ"
              : selectedRequest.status === "B"
              ? "Blacklist"
              : "รอการอนุมัติ"}
          </Header>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
            marginTop: "20px",
            marginLeft: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: COLOR.light_gray,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              <img
                src={
                  selectedRequest?.employee?.extra?.image
                    ? selectedRequest.employee.extra.image
                    : ICON.IconUser
                }
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
            <div>
              <p>สถานะคำขอ</p>
              <div
                style={{
                  display: "inline-block",
                  padding: "5px 15px",
                  borderRadius: "4px",
                  background:
                    selectedRequest.status === "X" || selectedRequest.status === "B"
                      ? COLOR.light_pink
                      : selectedRequest.status === "A"
                      ? "#c4eac5"
                      : COLOR.light_gray,
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  color:
                    selectedRequest.status === "X"
                      ? COLOR.red
                      : selectedRequest.status === "B"
                      ? "#d06500"
                      : selectedRequest.status === "A"
                      ? "#137e45"
                      : "black",
                }}
              >
                {selectedRequest.status === "S"
                  ? "รออนุมัติ"
                  : selectedRequest.status === "R"
                  ? "ส่งข้อมูลเข้ามาตรวจสอบใหม่"
                  : selectedRequest.status === "B"
                  ? "Blacklist"
                  : selectedRequest.status === "A"
                  ? "ได้รับการอนุมัติ"
                  : "ไม่อนุมัติ"}
              </div>
              {(selectedRequest.status === "X" || selectedRequest.status === "R") && (
                <div style={{ marginTop: "8px" }}>
                  {selectedRequest.status === "R" && (
                    <>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          color: COLOR.dark_orange,
                          fontWeight: "bold",
                          borderBottom: `2px solid ${COLOR.dark_orange}`,
                          cursor: "pointer",
                          marginBottom: "8px",
                          marginRight: "5px",
                        }}
                        onClick={() => setIsEditHistoryOpen(!isEditHistoryOpen)}
                      >
                        <img
                          src={ICON.IconEditHistory}
                          alt="Edit History"
                          style={{ width: "14px", height: "14px", marginRight: "5px" }}
                        />
                        <span>ประวัติการแก้ไขข้อมูล</span>
                      </div>
                      <span style={{ fontSize: "1.5em" }}>|</span>
                    </>
                  )}
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      color: COLOR.light_red,
                      fontWeight: "bold",
                      borderBottom: `2px solid ${COLOR.light_red}`,
                      cursor: "pointer",
                      marginLeft: "5px",
                    }}
                    onClick={() => setIsRejectReasonModalOpen(true)}
                  >
                    <img
                      src={ICON.IconRejectReason}
                      alt="Reject Reason"
                      style={{ width: "14px", height: "14px", marginRight: "5px" }}
                    />
                    <span>สาเหตุการปฏิเสธ</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Modal
            open={isRejectReasonModalOpen}
            onClose={() => setIsRejectReasonModalOpen(false)}
            size="small"
          >
            <Modal.Content>
              <Icon
                name="close"
                style={{ position: "absolute", top: "20px", right: "10px", cursor: "pointer" }}
                onClick={() => setIsRejectReasonModalOpen(false)}
                size="large"
              />
              <h3 style={{ display: "flex", alignItems: "center" }}>
                <img
                  src={ICON.IconReject}
                  alt="Reject"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "5px",
                    verticalAlign: "middle",
                  }}
                />
                สาเหตุที่ถูกปฏิเสธการสมัคร
              </h3>

              <Table celled style={{ marginBottom: "20px", maxHeight: "300px", overflowY: "auto" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ backgroundColor: "#ddd" }}>
                      วันที่ได้รับการปฏิเสธ
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ backgroundColor: COLOR.primary, color: "white" }}>
                      สาเหตุที่ถูกปฏิเสธ
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(selectedRequest.actions || [])
                    .filter((a: any) => a.action === "REJECT")
                    .map((a: any) => (
                      <Table.Row key={a.id}>
                        <Table.Cell>{moment(a.datetime).format("DD/MM/YYYY HH.mm")} น.</Table.Cell>
                        <Table.Cell>{a.comment || "-"}</Table.Cell>
                      </Table.Row>
                    ))}
                </Table.Body>
              </Table>
            </Modal.Content>
          </Modal>

          <Modal open={isEditHistoryOpen} onClose={() => setIsEditHistoryOpen(false)} size="large">
            <Modal.Content scrolling>
              <Icon
                name="close"
                style={{ position: "absolute", top: "20px", right: "10px", cursor: "pointer" }}
                onClick={() => setIsEditHistoryOpen(false)}
                size="large"
              />
              <h3 style={{ display: "flex", alignItems: "center" }}>ประวัติการแก้ไขข้อมูล</h3>

              {(() => {
                const filteredLogs = (props.auditLogList || [])
                  .filter((log: any) => {
                    if (log.edit_user?.id !== selectedRequest.owner || log.action !== "UPDATE")
                      return false;

                    const diffValues = log.diff?.values_changed || {};
                    const editedChange = diffValues["root['edited']"];
                    if (editedChange) {
                      const oldEdited = new Date(editedChange.old_value);
                      const newEdited = new Date(editedChange.new_value);
                      const diffMs = Math.abs(newEdited.getTime() - oldEdited.getTime());
                      const diffMinutes = diffMs / 1000 / 60;
                      if (diffMinutes < 1) return false;
                    }

                    const hasRealOldValue = Object.values(diffValues).some(
                      (change: any) =>
                        change?.old_value !== "" &&
                        change?.old_value !== null &&
                        change?.old_value !== undefined
                    );
                    return (
                      hasRealOldValue ||
                      Object.keys(log.diff?.iterable_item_added || {}).length > 0 ||
                      Object.keys(log.diff?.iterable_item_removed || {}).length > 0
                    );
                  })
                  .sort(
                    (a: any, b: any) =>
                      new Date(b.created).getTime() - new Date(a.created).getTime()
                  );

                if (filteredLogs.length === 0) {
                  return (
                    <div
                      style={{
                        backgroundColor: COLOR.light_pink,
                        border: "1px solid #ffb3c6",
                        borderRadius: "8px",
                        padding: "15px",
                        textAlign: "center",
                        color: COLOR.primary,
                        fontWeight: "bold",
                        marginTop: "10px",
                      }}
                    >
                      ไม่พบการแก้ไขข้อมูล
                    </div>
                  );
                }

                return filteredLogs.map((log: any) => {
                  const diffData = {
                    ...(log.diff?.values_changed || {}),
                    ...(log.diff?.type_changes || {}),
                    ...(log.diff?.iterable_item_added || {}),
                    ...(log.diff?.iterable_item_removed || {}),
                  };
                  const diffValues = log.diff?.values_changed || {};
                  const changedKeys = Object.keys(diffValues).filter((k) => k !== "root['edited']");
                  const extraValues = log.diff?.values_changed || {};
                  const iterableAdded = log.diff?.iterable_item_added || {};
                  const iterableRemoved = log.diff?.iterable_item_removed || {};
                  const extraRows: any[] = [];

                  const getSpecialtyName = (code: string) =>
                    props.specialtyList?.find((s: any) => s.code === code)?.name || code;

                  Object.keys(extraValues)
                    .filter((key) => key.startsWith("root['extra']['specialties']"))
                    .forEach((key) => {
                      const val = extraValues[key].new_value ?? "-";
                      const indexMatch = key.match(/\[(\d+)\]/);
                      const order = indexMatch ? Number(indexMatch[1]) + 1 : "-";
                      extraRows.push({
                        main: "ข้อมูลทางวิชาชีพ",
                        sub: `(ลำดับที่ ${order}) ความเชี่ยวชาญและกลุ่มอาการ`,
                        value: getSpecialtyName(val),
                      });
                    });

                  Object.entries(iterableAdded)
                    .filter(([path, val]: any) => path.startsWith("root['extra']['specialties']"))
                    .forEach(([path, val]: any) => {
                      const indexMatch = path.match(/\[(\d+)\]/);
                      const order = indexMatch ? Number(indexMatch[1]) + 1 : "-";
                      extraRows.push({
                        main: "ข้อมูลทางวิชาชีพ (เพิ่มข้อมูล)",
                        sub: `(ลำดับที่ ${order}) เพิ่มความเชี่ยวชาญและกลุ่มอาการ`,
                        value: getSpecialtyName(val),
                      });
                    });

                  Object.entries(iterableRemoved)
                    .filter(([path, val]: any) => path.startsWith("root['extra']['specialties']"))
                    .forEach(([path, val]: any) => {
                      const indexMatch = path.match(/\[(\d+)\]/);
                      const order = indexMatch ? Number(indexMatch[1]) + 1 : "-";
                      extraRows.push({
                        main: "ข้อมูลทางวิชาชีพ (ลบข้อมูล)",
                        sub: `(ลำดับที่ ${order}) ลบความเชี่ยวชาญและกลุ่มอาการ`,
                        value: getSpecialtyName(val),
                      });
                    });

                  if (extraValues["root['extra']['work_address']"]) {
                    const val = extraValues["root['extra']['work_address']"].new_value ?? "-";
                    extraRows.push({
                      main: "ข้อมูลทางวิชาชีพ",
                      sub: "สถานที่ทำงาน",
                      value: val,
                    });
                  }

                  const hasRealChange =
                    changedKeys.length > 0 ||
                    Object.keys(iterableAdded).length > 0 ||
                    Object.keys(iterableRemoved).length > 0;

                  if (!hasRealChange) return false;

                  const filteredEntries = Object.entries(diffData).filter(([path]) => {
                    const fieldName = path.replace(/root\['|'\]/g, "");
                    return (
                      !EXCLUDE_FIELDS.includes(fieldName) &&
                      !(iterableAdded[path] || iterableRemoved[path])
                    );
                  });

                  const educationDiffs = Object.entries(diffData).filter(([path]) =>
                    path.startsWith("root['extra']['educations']")
                  );
                  const workDiffs = Object.entries(diffData).filter(([path]) =>
                    path.startsWith("root['extra']['works']")
                  );

                  const getIndexes = (diffs: any[], type: "educations" | "works") => {
                    const regex = new RegExp(`root\\['extra'\\]\\['${type}'\\]\\[(\\d+)\\]`);
                    return [
                      ...new Set(
                        diffs
                          .map(([path]) => {
                            const match = path.match(regex);
                            return match ? Number(match[1]) : null;
                          })
                          .filter((x) => x !== null)
                      ),
                    ];
                  };

                  const buildRows = (
                    items: any[],
                    type: "education" | "work",
                    indexes: number[],
                    actionText: string = ""
                  ) => {
                    return items.flatMap((item, idx) => {
                      const rows: any[] = [];
                      const prefix = type === "education" ? "ประวัติการศึกษา" : "ประวัติการทำงาน";
                      const order = indexes[idx] + 1;

                      const mainPrefix = actionText ? `${prefix} (${actionText})` : prefix;

                      if (type === "education") {
                        if (item.category)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) หมวดการศึกษา`,
                            value: getCategoryName(item.category),
                          });
                        if (item.degree_name)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ชื่อปริญญา`,
                            value: item.degree_name,
                          });
                        if (item.faculty)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) คณะ`,
                            value: item.faculty,
                          });
                        if (item.graduate_year)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ปีที่สำเร็จการศึกษา`,
                            value: item.graduate_year,
                          });
                        if (item.major)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) สาขา`,
                            value: item.major,
                          });
                        if (item.school)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) สถาบัน`,
                            value: getSchoolName(item.school),
                          });
                        if (item.level)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ระดับ`,
                            value: getEducationLevelName(item.level),
                          });
                        if (item.status)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) สถานภาพการศึกษา`,
                            value: getEducationStatusName(item.status),
                          });
                      } else if (type === "work") {
                        const startMonth = thaiMonths[item.start_month] || "-";
                        const startYear = item.start_year || "-";
                        const endMonth = thaiMonths[item.end_month] || "-";
                        const endYear = item.end_year || "-";
                        if (item.start_month && item.start_year)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) เริ่มทำงานตั้งแต่`,
                            value: `${startMonth} ${startYear}`,
                          });
                        if (item.end_month && item.end_year)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ทำงานถึง`,
                            value: `${endMonth} ${endYear}`,
                          });
                        if (item.company)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ชื่อบริษัท`,
                            value: item.company,
                          });
                        if (item.address)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ที่อยู่`,
                            value: item.address,
                          });
                        if (item.level)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ระดับ`,
                            value: getJobLevelText(item.level),
                          });
                        if (item.type)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ประเภทงาน`,
                            value: getJobTypeText(item.type),
                          });
                        if (item.position)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ตำแหน่งงาน`,
                            value: item.position,
                          });
                        if (item.description)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) รายละเอียดงาน`,
                            value: item.description,
                          });
                      }
                      return rows;
                    });
                  };

                  const eduIndexes = getIndexes(educationDiffs, "educations");
                  const workIndexes = getIndexes(workDiffs, "works");

                  const educationRows = buildRows(
                    eduIndexes.map((i) => {
                      const obj: any = {};
                      educationDiffs
                        .filter(([p]) => p.includes(`[${i}]`))
                        .forEach(([path, val]: any) => {
                          const key = path.split("['").pop()?.replace("']", "");
                          obj[key!] = val.new_value;
                        });
                      return obj;
                    }),
                    "education",
                    eduIndexes
                  );

                  const educationAddedRows = buildRows(
                    Object.values(iterableAdded).filter((path: any) => path.school !== undefined),
                    "education",
                    Object.keys(iterableAdded).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "เพิ่มข้อมูล"
                  );

                  const educationRemovedRows = buildRows(
                    Object.values(iterableRemoved).filter((path: any) => path.school !== undefined),
                    "education",
                    Object.keys(iterableRemoved).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "ลบข้อมูล"
                  );

                  const allEducationRows = [
                    ...educationRows,
                    ...educationAddedRows,
                    ...educationRemovedRows,
                  ];

                  const workRows = buildRows(
                    workIndexes.map((i) => {
                      const obj: any = {};
                      workDiffs
                        .filter(([p]) => p.includes(`[${i}]`))
                        .forEach(([path, val]: any) => {
                          const key = path.split("['").pop()?.replace("']", "");
                          obj[key!] = val.new_value;
                        });
                      return obj;
                    }),
                    "work",
                    workIndexes
                  );

                  const workAddedRows = buildRows(
                    Object.values(iterableAdded).filter((path: any) => path.company !== undefined),
                    "work",
                    Object.keys(iterableAdded).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "เพิ่มข้อมูล"
                  );

                  const workRemovedRows = buildRows(
                    Object.values(iterableRemoved).filter(
                      (path: any) => path.company !== undefined
                    ),
                    "work",
                    Object.keys(iterableRemoved).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "ลบข้อมูล"
                  );

                  const allWorkRows = [...workRows, ...workAddedRows, ...workRemovedRows];

                  return (
                    <div
                      key={log.id}
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "5px",
                          fontWeight: "bold",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <img
                            src={ICON.IconEdit}
                            alt="Edit"
                            style={{
                              width: "20px",
                              height: "20px",
                              marginRight: "5px",
                              verticalAlign: "middle",
                            }}
                          />
                          ส่งข้อมูลเข้ามาตรวจสอบใหม่
                        </div>
                        <div>
                          วันที่ส่งตรวจสอบ
                          <span style={{ color: COLOR.primary, marginLeft: "5px" }}>
                            {moment(log.created).format("DD/MM/YYYY HH:mm")} น.
                          </span>
                        </div>
                      </div>

                      <Table celled compact>
                        <Table.Header style={{ textAlign: "center" }}>
                          <Table.Row>
                            <Table.HeaderCell>หัวข้อหลักของข้อมูล</Table.HeaderCell>
                            <Table.HeaderCell>ส่วนที่แก้ไข</Table.HeaderCell>
                            <Table.HeaderCell
                              style={{ backgroundColor: COLOR.primary, color: "white" }}
                            >
                              ข้อมูลที่แก้ไข
                            </Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {filteredEntries.map(([fieldPath, value]: any, index: number) => {
                            const fieldName = fieldPath.replace(/root\['|'\]/g, "");
                            const mapping = EDIT_DATA_MAPPING[fieldName];
                            if (!mapping) return null;

                            let newVal = value.new_value ?? "-";

                            if (fieldName === "birth_date" && newVal) {
                              newVal = moment(newVal).format("DD/MM/YYYY");
                            }
                            if (fieldName === "gender" && newVal) {
                              newVal = newVal === "M" ? "ชาย" : newVal === "F" ? "หญิง" : newVal;
                            }

                            return (
                              <Table.Row key={`main-${index}`}>
                                <Table.Cell>{mapping.main}</Table.Cell>
                                <Table.Cell>{mapping.sub}</Table.Cell>
                                <Table.Cell>{newVal.toString()}</Table.Cell>
                              </Table.Row>
                            );
                          })}

                          {extraRows.map((row, i) => (
                            <Table.Row key={`extra-${i}`}>
                              <Table.Cell>{row.main}</Table.Cell>
                              <Table.Cell>{row.sub}</Table.Cell>
                              <Table.Cell>{row.value}</Table.Cell>
                            </Table.Row>
                          ))}

                          {allEducationRows.map((row: any, i: any) => (
                            <Table.Row key={`edu-${i}`}>
                              <Table.Cell>{row.main}</Table.Cell>
                              <Table.Cell>{row.sub}</Table.Cell>
                              <Table.Cell>{row.value}</Table.Cell>
                            </Table.Row>
                          ))}

                          {allWorkRows.map((row: any, i: any) => (
                            <Table.Row key={`work-${i}`}>
                              <Table.Cell>{row.main}</Table.Cell>
                              <Table.Cell>{row.sub}</Table.Cell>
                              <Table.Cell>{row.value}</Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  );
                });
              })()}
            </Modal.Content>
          </Modal>

          {selectedRequest.status !== "X" &&
          selectedRequest.status !== "B" &&
          selectedRequest.status !== "A" ? (
            <div style={{ display: "flex", gap: "1rem" }}>
              <Button
                style={{
                  border: `1px solid ${COLOR.green}`,
                  color: COLOR.green,
                  backgroundColor: "white",
                }}
                onClick={() => setIsConfirmModalOpen(true)}
              >
                อนุมัติ
              </Button>
              <Button
                style={{
                  border: `1px solid ${COLOR.red}`,
                  color: COLOR.red,
                  backgroundColor: "white",
                }}
                onClick={() => {
                  setRejectTarget(selectedRequest);
                  setIsRejectModalOpen(true);
                }}
              >
                ไม่อนุมัติ
              </Button>
            </div>
          ) : selectedRequest.status === "X" ? (
            <div
              style={{
                padding: "10px 15px",
                backgroundColor: "white",
                color: COLOR.primary,
                border: `1px solid ${COLOR.ccc_gray}`,
                borderRadius: "4px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                minWidth: "500px",
              }}
            >
              <div style={{ fontSize: "16px" }}>สาเหตุที่ถูกปฏิเสธ</div>
              <div
                style={{
                  color: "black",
                  fontWeight: "normal",
                  marginLeft: "10px",
                  marginBottom: "1rem",
                }}
              >
                {latestReject ? latestReject.comment : "-"}
              </div>
            </div>
          ) : selectedRequest.status === "B" ? (
            <div
              style={{
                padding: "10px 15px",
                backgroundColor: "white",
                color: "black",
                border: `1px solid ${COLOR.ccc_gray}`,
                borderRadius: "4px",
                fontWeight: "bold",
                display: "flex",
                fontSize: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span>จำนวนที่ส่งคำขอ</span>
                <div style={{ color: COLOR.primary, fontWeight: "bold" }}>
                  {(selectedRequest.actions || []).filter((a: any) => a.action === "SUBMIT").length}
                </div>
                <span>ครั้ง</span>
              </div>
            </div>
          ) : null}
          <Modal
            open={isConfirmModalOpen}
            onClose={() => setIsConfirmModalOpen(false)}
            size="mini"
            style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}
          >
            <Modal.Content>
              <h3 style={{ color: COLOR.primary }}>ยืนยันการอนุมัติ</h3>
              <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                {selectedRequest.employee?.full_name?.trim() ||
                  `${selectedRequest.employee?.first_name ?? ""} ${
                    selectedRequest.employee?.last_name ?? ""
                  }`.trim()}
              </div>
              สมัครเข้าเป็นสมาชิก MeHealth
              <div
                style={{
                  display: "flex",
                  marginTop: "20px",
                  gap: "10px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Button onClick={() => setIsConfirmModalOpen(false)}>ยกเลิก</Button>
                <Button
                  style={{ backgroundColor: COLOR.primary, color: "white" }}
                  loading={props.buttonLoadCheck.approveTherapist === "LOADING"}
                  onClick={async () => {
                    props.onEvent({
                      message: "HandleApproveTherapist",
                      params: { therapistId: selectedRequest.id },
                    });
                    setSelectedRequest({
                      ...selectedRequest,
                      status: "A",
                      actions: [
                        ...(selectedRequest.actions || []),
                        { action: "APPROVE", datetime: new Date() },
                      ],
                    });
                    setIsConfirmModalOpen(false);
                  }}
                >
                  ยืนยัน
                </Button>
              </div>
            </Modal.Content>
          </Modal>
          <Modal open={isRejectModalOpen} onClose={() => setIsRejectModalOpen(false)} size="small">
            <Modal.Content>
              ระบุเหตุผลให้นักกายภาพทราบถึงสาเหตุในการโดนปฏิเสธ
              <span style={{ color: "#db2828" }}>*</span>
              <TextArea
                placeholder="โปรดระบุเหตุผล..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: "100px",
                  marginTop: "10px",
                  padding: "5px",
                  resize: "none",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                <Button
                  style={{
                    backgroundColor: COLOR.primary,
                    color: "white",
                  }}
                  loading={isRejectLoading}
                  disabled={isRejectLoading || rejectReason.trim() === ""}
                  onClick={async () => {
                    if (rejectReason.trim() === "") return;
                    setRejectLoading(true);

                    await props.onEvent({
                      message: "HandleRejectTherapist",
                      params: {
                        therapistId: selectedRequest.id,
                        comment: rejectReason.trim(),
                      },
                    });

                    setRejectLoading(false);
                    setSelectedRequest({
                      ...selectedRequest,
                      status: "X",
                      actions: [
                        ...(selectedRequest.actions || []),
                        { action: "REJECT", comment: rejectReason.trim(), datetime: new Date() },
                      ],
                    });
                    setRejectReason("");
                    setIsRejectModalOpen(false);
                  }}
                >
                  ส่งข้อความไปยังนักกายภาพ
                </Button>
                <Button
                  onClick={() => {
                    setRejectReason("");
                    setIsRejectModalOpen(false);
                  }}
                >
                  ปิดหน้าต่าง
                </Button>
              </div>
            </Modal.Content>
          </Modal>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2rem",
            marginLeft: "20px",
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          <div>
            <div style={{ minHeight: "200px" }}>
              <h4 style={{ color: COLOR.primary }}>ข้อมูลส่วนตัว</h4>
              {[
                {
                  label: "ชื่อ-นามสกุล",
                  value:
                    selectedRequest.employee?.full_name?.trim() ||
                    `${selectedRequest.employee?.first_name ?? ""} ${
                      selectedRequest.employee?.last_name ?? ""
                    }`.trim(),
                },
                {
                  label: "วัน/เดือน/ปีเกิด",
                  value: formatDate(selectedRequest.employee?.birth_date),
                },
                { label: "เลขที่บัตรประชาชน", value: selectedRequest.employee?.citizen_no },
                {
                  label: "เพศ",
                  value: selectedRequest.employee?.gender === "M" ? "Male" : "Female",
                },
                { label: "อีเมล์", value: selectedRequest.employee?.email },
                { label: "เบอร์โทร", value: selectedRequest.employee?.phone_no },
              ].map((item, idx) => (
                <div key={idx} style={{ display: "flex", marginBottom: "0.5rem" }}>
                  <div style={{ width: "150px", fontWeight: "bold", marginLeft: "10px" }}>
                    {item.label}
                  </div>
                  <div>{item.value || "-"}</div>
                </div>
              ))}
            </div>

            <div style={{ minHeight: "200px" }}>
              <h4 style={{ color: COLOR.primary, marginTop: "2rem" }}>ประวัติการศึกษา</h4>
              {selectedRequest.employee?.extra?.educations?.map((edu: any, idx: number) => {
                const educationLevelName =
                  props.educationList?.find((item: any) => item.code === edu.level)?.name || "-";
                return (
                  <div key={idx} style={{ display: "flex", position: "relative" }}>
                    <div style={{ marginLeft: "10px", paddingRight: "5px" }}>
                      <div style={{ fontWeight: "bold" }}>ลำดับที่ {idx + 1}</div>
                    </div>
                    <div
                      style={{
                        width: "40px",
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: COLOR.primary,
                          zIndex: 1,
                        }}
                      ></div>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          width: "2px",
                          borderLeft: `2px dotted ${COLOR.primary}`,
                          margin: "0 auto",
                        }}
                      ></div>
                    </div>
                    <div style={{ flex: 1, paddingLeft: "5px" }}>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "130px" }}>ระดับการศึกษา</strong>
                        <div>{educationLevelName}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "130px" }}>สถาบันการศึกษา</strong>
                        <div>{getSchoolName(edu.school) || "-"}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "130px" }}>คณะ</strong>
                        <div>{edu.faculty || "-"}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "130px" }}>สาขา</strong>
                        <div>{edu.major || "-"}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "1.3rem" }}>
                        <strong style={{ width: "130px" }}>ปีที่สำเร็จ</strong>
                        <div>{edu.graduate_year || "-"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ minHeight: "200px" }}>
              <h4 style={{ color: COLOR.primary }}>ข้อมูลทางวิชาชีพ</h4>
              <div style={{ marginBottom: "1rem", marginLeft: "10px" }}>
                <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                  <div style={{ width: "180px", fontWeight: "bold" }}>เลขที่ใบประกอบวิชาชีพ</div>
                  <div>{selectedRequest.employee?.licence || "-"}</div>
                </div>
                <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                  <div style={{ width: "180px", fontWeight: "bold" }}>ความเชี่ยวชาญ</div>
                  <div style={{ minWidth: "300px", width: "50%" }}>{specialtyNames}</div>
                </div>
                <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                  <div style={{ width: "180px", fontWeight: "bold" }}>เอกสารที่เกี่ยวข้อง</div>
                  <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
                    <Button
                      onClick={() => handlePreviewDocument("LICENCE")}
                      style={{ backgroundColor: COLOR.light_pink, color: "black" }}
                    >
                      กดเพื่อดูใบประกอบวิชาชีพ
                    </Button>
                    <Button
                      onClick={() => handlePreviewDocument("CITIZEN_ID")}
                      style={{ backgroundColor: COLOR.light_pink, color: "black" }}
                    >
                      กดเพื่อดูบัตรประชาชน
                    </Button>
                  </div>
                </div>
                <Modal
                  open={previewOpen}
                  onClose={() => {
                    setPreviewOpen(false);
                    setZoom(1);
                  }}
                  size="small"
                >
                  <Modal.Content>
                    <div style={{ display: "flex", justifyContent: "right", alignItems: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.2rem",
                          backgroundColor: "#e0e0e0",
                          padding: "0.1rem",
                          borderRadius: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          icon
                          onClick={handleZoomOut}
                          size="tiny"
                          style={{
                            backgroundColor: "white",
                            borderRadius: "50%",
                            padding: "0.1rem",
                          }}
                        >
                          <Icon name="minus" />
                        </Button>
                        <Button
                          icon
                          onClick={handleZoomIn}
                          size="tiny"
                          style={{
                            backgroundColor: "white",
                            borderRadius: "50%",
                            padding: "0.1rem",
                          }}
                        >
                          <Icon name="plus" />
                        </Button>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            paddingBottom: "0.2rem",
                          }}
                        >
                          <Icon name="zoom" />
                        </div>
                      </div>

                      <Button
                        icon
                        onClick={() => {
                          setPreviewOpen(false);
                          setZoom(1);
                        }}
                        size="large"
                        style={{
                          backgroundColor: "white",
                          color: "black",
                        }}
                      >
                        <Icon name="close" />
                      </Button>
                    </div>
                    {previewUrl ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "70vh",
                          overflow: "auto",
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="document preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            transform: `scale(${zoom})`,
                            transformOrigin: "top left",
                            transition: "transform 0.2s",
                          }}
                        />
                      </div>
                    ) : (
                      <p>เอกสารไม่พบ</p>
                    )}
                  </Modal.Content>
                </Modal>
              </div>
            </div>

            <div style={{ minHeight: "200px" }}>
              <h4 style={{ color: COLOR.primary, marginTop: "2rem" }}>ประวัติการทำงาน</h4>
              {selectedRequest.employee?.extra?.works?.map((work: any, idx: number) => {
                const startMonth = thaiMonths[work.start_month] || "-";
                const startYear = work.start_year || "-";
                const endMonth = thaiMonths[work.end_month] || "-";
                const endYear = work.end_year || "-";

                const period = `${startMonth} ${startYear} - ${endMonth} ${endYear}`;

                return (
                  <div key={idx} style={{ display: "flex", position: "relative" }}>
                    <div style={{ marginLeft: "10px", paddingRight: "5px" }}>
                      <div style={{ fontWeight: "bold" }}>ลำดับที่ {idx + 1}</div>
                    </div>
                    <div
                      style={{
                        width: "40px",
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          width: "2px",
                          borderLeft: `3px solid ${COLOR.primary}`,
                          marginBottom: "10px",
                        }}
                      ></div>
                    </div>
                    <div style={{ flex: 1, paddingLeft: "5px" }}>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "90px" }}>ช่วงเวลา</strong>
                        <div>{period || "-"}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "1rem" }}>
                        <strong style={{ width: "90px" }}>ชื่อบริษัท</strong>
                        <div>{work.company || "-"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPendingTable = (data: any[], setSelectedRequest: any, emptyRows: any[]) => (
    <Table celled structured selectable style={{ textAlign: "center" }}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>ชื่อผู้ส่งคำขอ</Table.HeaderCell>
          <Table.HeaderCell>วันที่ส่งคำขอ</Table.HeaderCell>
          <Table.HeaderCell>สถานะคำขอ</Table.HeaderCell>
          <Table.HeaderCell>การตรวจสอบ</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((therapist: any) => (
          <Table.Row key={therapist.id}>
            <Table.Cell style={{ textAlign: "left" }}>
              {therapist.employee.full_name?.trim()
                ? therapist.employee.full_name
                : `${therapist.employee.first_name ?? ""} ${
                    therapist.employee.last_name ?? ""
                  }`.trim()}
            </Table.Cell>
            <Table.Cell>
              {moment(therapist.last_submit_at).format("DD/MM/YYYY\u00A0\u00A0\u00A0HH.mm น.")}
            </Table.Cell>
            <Table.Cell>
              {therapist.status === "S"
                ? "รออนุมัติ"
                : therapist.status === "R"
                ? "ส่งข้อมูลเข้ามาตรวจสอบใหม่"
                : "ได้รับการอนุมัติ"}
            </Table.Cell>
            <Table.Cell>
              <Button
                size="small"
                style={{
                  border: `1px solid ${COLOR.primary}`,
                  color: COLOR.primary,
                  backgroundColor: "white",
                }}
                onClick={() => setSelectedRequest(therapist)}
              >
                ตรวจสอบข้อมูล
              </Button>
            </Table.Cell>
          </Table.Row>
        ))}
        {emptyRows.map((_, idx) => (
          <Table.Row key={`empty-${idx}`} style={{ height: "50px" }}>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  const renderRejectTable = (data: any[], setSelectedRequest: any, emptyRowsCount: number) => (
    <Table celled structured selectable style={{ textAlign: "center" }}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>ชื่อผู้ส่งคำขอ</Table.HeaderCell>
          <Table.HeaderCell>วันที่ส่งคำขอ</Table.HeaderCell>
          <Table.HeaderCell>วันที่ได้รับการปฏิเสธ</Table.HeaderCell>
          <Table.HeaderCell>เหตุผลที่ปฏิเสธ</Table.HeaderCell>
          <Table.HeaderCell>การตรวจสอบ</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((therapist: any) => {
          const actions = therapist?.actions || [];
          const rejectActions = actions.filter((a: any) => a.action === "REJECT");
          const latestRejectAction =
            rejectActions.length > 0 ? rejectActions[rejectActions.length - 1] : null;

          return (
            <Table.Row key={therapist.id}>
              <Table.Cell style={{ textAlign: "left" }}>
                {therapist.employee.full_name?.trim()
                  ? therapist.employee.full_name
                  : `${therapist.employee.first_name ?? ""} ${
                      therapist.employee.last_name ?? ""
                    }`.trim()}
              </Table.Cell>
              <Table.Cell>
                {moment(therapist.last_submit_at).format("DD/MM/YYYY\u00A0\u00A0\u00A0HH.mm น.")}
              </Table.Cell>
              <Table.Cell>
                {moment(therapist.last_reject_at).format("DD/MM/YYYY\u00A0\u00A0\u00A0HH.mm น.")}
              </Table.Cell>
              <Table.Cell>{latestRejectAction ? latestRejectAction.comment : "-"}</Table.Cell>
              <Table.Cell>
                <Button
                  size="small"
                  style={{
                    border: `1px solid ${COLOR.primary}`,
                    color: COLOR.primary,
                    backgroundColor: "white",
                  }}
                  onClick={() => setSelectedRequest(therapist)}
                >
                  ตรวจสอบข้อมูล
                </Button>
              </Table.Cell>
            </Table.Row>
          );
        })}

        {Array.from({ length: emptyRowsCount }).map((_, idx) => (
          <Table.Row key={`empty-rejected-${idx}`} style={{ height: "50px" }}>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  const renderBlacklistTable = (data: any[], setSelectedRequest: any, emptyRowsCount: number) => (
    <Table celled structured selectable style={{ textAlign: "center" }}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>ชื่อผู้ส่งคำขอ</Table.HeaderCell>
          <Table.HeaderCell>วันที่ได้รับการปฏิเสธ</Table.HeaderCell>
          <Table.HeaderCell>เหตุผลที่ปฏิเสธ</Table.HeaderCell>
          <Table.HeaderCell>การตรวจสอบ</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((therapist: any) => {
          const actions = therapist?.actions || [];
          const rejectActions = actions.filter((a: any) => a.action === "REJECT");
          const latestRejectAction =
            rejectActions.length > 0 ? rejectActions[rejectActions.length - 1] : null;

          return (
            <Table.Row key={therapist.id}>
              <Table.Cell style={{ textAlign: "left" }}>
                {therapist.employee.full_name?.trim()
                  ? therapist.employee.full_name
                  : `${therapist.employee.first_name ?? ""} ${
                      therapist.employee.last_name ?? ""
                    }`.trim()}
              </Table.Cell>
              <Table.Cell>
                {moment(therapist.last_blacklist_at).format("DD/MM/YYYY\u00A0\u00A0\u00A0HH.mm น.")}
              </Table.Cell>
              <Table.Cell>{latestRejectAction ? latestRejectAction.comment : "-"}</Table.Cell>
              <Table.Cell>
                <Button
                  size="small"
                  style={{
                    border: `1px solid ${COLOR.primary}`,
                    color: COLOR.primary,
                    backgroundColor: "white",
                  }}
                  onClick={() => setSelectedRequest(therapist)}
                >
                  ตรวจสอบข้อมูล
                </Button>
              </Table.Cell>
            </Table.Row>
          );
        })}
        {Array.from({ length: emptyRowsCount }).map((_, idx) => (
          <Table.Row key={`empty-blacklist-${idx}`} style={{ height: "50px" }}>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  const FilterDate = ({
    value,
    onChange,
    placeholder = "dd/mm/yyyy",
  }: {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
  }) => {
    const [localValue, setLocalValue] = React.useState(
      value ? moment(value).add(543, "years").format("DD/MM/YYYY") : ""
    );

    React.useEffect(() => {
      setLocalValue(value ? moment(value).add(543, "years").format("DD/MM/YYYY") : "");
    }, [value]);

    return (
      <DateTextBox
        placeholder={placeholder}
        value={localValue}
        onChange={(val) => {
          setLocalValue(val);

          const isFullDate = /^\d{2}\/\d{2}\/\d{4}$/.test(val);
          if (!isFullDate) return;

          const date = parseBeOrAdDate(val);
          if (date && date.isValid()) {
            onChange(date.format("YYYY-MM-DD"));
          }
        }}
      />
    );
  };

  useEffect(() => {
    props.onEvent({
      message: "HandleGetTherapistList",
    });
    props.onEvent({
      message: "HandleGetEducationList",
    });
    props.onEvent({
      message: "HandleGetSpecialtyList",
    });
    props.onEvent({
      message: "HandleGetClinicalTermSet",
    });
    props.onEvent({
      message: "HandleGetAuditLogList",
      params: { model: "employee" },
    });
  }, []);

  useEffect(() => {
    if (selectedRequest?.employee?.id) {
      props.onEvent({
        message: "HandleGetEmployeeDocuments",
        params: { employee_id: selectedRequest.employee?.id },
      });
    }
  }, [selectedRequest?.employee?.id]);

  useEffect(() => {
    const initialData = (props.currentTherapists || []).filter(
      (t: any) => t.status === "S" || t.status === "R"
    );
    setFilteredTherapists(initialData);
  }, [props.currentTherapists]);

  useEffect(() => {
    props.onEvent({
      message: "HandleGetTherapistList",
    });
  }, [props.currentTherapists?.status]);

  if (selectedRequest) {
    return renderPersonalDetail(selectedRequest, setSelectedRequest);
  }

  return (
    <div className="card-physio-membership" style={{ padding: "15px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          padding: "0 15px",
        }}
      >
        <Header style={{ color: "rgb(79,79,79)", margin: 0 }} as="h2">
          คำขอเป็นสมาชิกนักกายภาพ MeHealth
        </Header>
      </div>

      <Tab
        activeIndex={activeTabIndex}
        menu={{ pointing: true, secondary: true }}
        panes={[
          {
            menuItem: {
              key: "pending",
              content: (
                <div>
                  <span>รอการอนุมัติ</span>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <span style={{ marginRight: "1rem" }}>ชื่อ/นามสกุล</span>
                      <Input
                        placeholder="ระบุคำค้นหา"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ minWidth: "300px" }}
                      />
                    </div>
                    <div>
                      <span style={{ marginRight: "1rem" }}>วันที่</span>
                      <FilterDate value={filterStartDate} onChange={setFilterStartDate} />
                      <span style={{ marginRight: "1rem", marginLeft: "1rem" }}>ถึง</span>
                      <FilterDate value={filterEndDate} onChange={setFilterEndDate} />
                    </div>
                    <div>
                      <span style={{ marginRight: "1rem" }}>สถานะคำขอ</span>
                      <Dropdown
                        search
                        selection
                        options={STATUS_OPTIONS}
                        value={filterStatus}
                        onChange={(e, { value }: any) => setFilterStatus(value)}
                      />
                    </div>
                    <Button
                      style={{ color: "white", backgroundColor: COLOR.primary }}
                      loading={props.buttonLoadCheck?.searchTherapists === "LOADING"}
                      onClick={handleSearchClick}
                    >
                      ค้นหา
                    </Button>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    {renderPendingTable(currentPendingData, setSelectedRequest, emptyRows)}
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },

          {
            menuItem: {
              key: "rejected",
              content: (
                <div>
                  <span>ถูกปฏิเสธ</span>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "1rem",
                    }}
                  >
                    <div>
                      <span style={{ marginRight: "0.5rem" }}>ชื่อ/นามสกุล</span>
                      <Input
                        placeholder="ระบุคำค้นหา"
                        value={rejectedFilterName}
                        onChange={(e) => setRejectedFilterName(e.target.value)}
                        style={{ minWidth: "200px" }}
                      />
                    </div>
                    <div>
                      <span style={{ marginRight: "0.5rem" }}>วันที่ส่งคำขอ</span>
                      <FilterDate
                        value={rejectedFilterSubmitFrom}
                        onChange={setRejectedFilterSubmitFrom}
                      />
                      <span style={{ margin: "0 0.5rem" }}>ถึง</span>
                      <FilterDate
                        value={rejectedFilterSubmitTo}
                        onChange={setRejectedFilterSubmitTo}
                      />
                    </div>
                    <div>
                      <span style={{ marginRight: "0.5rem" }}>วันที่ปฏิเสธ</span>
                      <FilterDate
                        value={rejectedFilterRejectFrom}
                        onChange={setRejectedFilterRejectFrom}
                      />
                      <span style={{ margin: "0 0.5rem" }}>ถึง</span>
                      <FilterDate
                        value={rejectedFilterRejectTo}
                        onChange={setRejectedFilterRejectTo}
                      />
                    </div>
                    <Button
                      style={{ color: "white", backgroundColor: COLOR.primary }}
                      loading={props.buttonLoadCheck?.searchTherapists === "LOADING"}
                      onClick={handleRejectSearchClick}
                    >
                      ค้นหา
                    </Button>
                  </div>
                  {renderRejectTable(currentRejectedData, setSelectedRequest, 15)}

                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={rejectedActivePage}
                      totalPages={totalRejectedPages}
                      onPageChange={handleRejectedPageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },

          {
            menuItem: {
              key: "blacklist",
              content: (
                <div>
                  <span>Blacklist</span>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "1rem",
                    }}
                  >
                    <div>
                      <span style={{ marginRight: "0.5rem" }}>ชื่อ/นามสกุล</span>
                      <Input
                        placeholder="ระบุคำค้นหา"
                        value={blacklistFilterName}
                        onChange={(e) => setBlacklistFilterName(e.target.value)}
                        style={{ minWidth: "200px" }}
                      />
                    </div>
                    <div>
                      <span style={{ marginRight: "0.5rem" }}>วันที่ส่งคำขอ</span>
                      <FilterDate
                        value={blacklistFilterSubmitFrom}
                        onChange={setBlacklistFilterSubmitFrom}
                      />
                      <span style={{ margin: "0 0.5rem" }}>ถึง</span>
                      <FilterDate
                        value={blacklistFilterSubmitTo}
                        onChange={setBlacklistFilterSubmitTo}
                      />
                    </div>
                    <div>
                      <span style={{ marginRight: "0.5rem" }}>วันที่ได้รับการปฏิเสธ</span>
                      <FilterDate
                        value={blacklistFilterRejectFrom}
                        onChange={setBlacklistFilterRejectFrom}
                      />
                      <span style={{ margin: "0 0.5rem" }}>ถึง</span>
                      <FilterDate
                        value={blacklistFilterRejectTo}
                        onChange={setBlacklistFilterRejectTo}
                      />
                    </div>
                    <Button
                      style={{ color: "white", backgroundColor: COLOR.primary }}
                      loading={props.buttonLoadCheck?.searchTherapists === "LOADING"}
                      onClick={handleBlacklistSearchClick}
                    >
                      ค้นหา
                    </Button>
                  </div>

                  {renderBlacklistTable(currentBlacklistData, setSelectedRequest, 15)}

                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={blacklistActivePage}
                      totalPages={totalBlacklistPages}
                      onPageChange={handleBlacklistPageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
        ]}
        onTabChange={handleTabChange}
      />
    </div>
  );
};

export default CardPhysiotherapistSignUpRequest;
